import { EventEmitter, Injectable } from '@angular/core';
import { RateInterface } from '../../layout/interfaces/rate-interface';
import { AmountInterface } from '../../layout/interfaces/amount-interface';
import { SessionStorageService } from './session-storage.service';

/**
 * this service contains all the information about daily new rates.
 * Its purpose is to stabilize all visible rendered currencies by providing the latest known rates.
 */
@Injectable({
  providedIn: 'root'
})
export class RatesService {
  private newestAmounts: Array<AmountInterface> = [];
  private sessionStorageName = 'currencyrates';

  /**
   * emits when newer rates are found and written to the storage.
   * You can use this event to rerender your ChangeDetection.onPush Components
   */
  readonly rateChangeEvent = new EventEmitter<RateInterface>();

  constructor(private sessionStorage: SessionStorageService) {
    const result = this.sessionStorage.read(this.sessionStorageName);
    if (result) {
      try {
        this.newestAmounts = JSON.parse(result);
        this.newestAmounts.forEach(amount => {
          if (!amount || !amount.currency) {
            this.clearRates();
          }
        });
      } catch {
        this.clearRates();
      }
    }
  }

  /**
   * dates are refreshed at UTC+0 00:01:00 (which is the same and equals to GMT+1 = 01:01:00 CEST = 02:01:00)
   * returns a timestamp from today, UTC+0 now including the tolerance that the backend needs to guarantee an update of the rates
   * TODO: the tolerance of one minute seems to be too short. Updates sometimes happen half an hour to 45min after UTC+0 00:01:00
   * @returns the seconds UTC
   */
  getTimestampToCompare(): number {
    const date = new Date();
    date.setUTCHours(0, 1, 0, 0);
    const timestampWhenStale = date.getTime() / 1000;
    return timestampWhenStale; // UTC+1 yesterday with 1min tolerance
  }

  /**
   * all rates will be refreshed at UTC+1 00:01:00 (GMT+1 = 01:01:00 CEST = 02:01:00)
   * that means that all valid dates are refreshed in x seconds arount the world (SAME countdown)
   * TODO: the tolerance of one minute seems to be too short. Updates sometimes happen half an hour to 45min after UTC+0 00:01:00
   * @returns the time in seconds when new rates are available
   */
  getTimeUntilStale(): number {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setUTCHours(0, 1, 0, 0);
    const tomTime = tomorrow.getTime() / 1000;
    return tomTime - Date.now() / 1000; // this must be calculated to now
  }

  /**
   * patches an amount object with the newest rates currently known and returns them
   * @param amount the amount to patch
   */
  patchRate(amount: AmountInterface | number): AmountInterface {
    const cachedAmount = this.findNewestAmountForCurrency(
      typeof amount === 'number' ? 'EUR' : amount.currency.code
    );
    if (typeof amount === 'number') {
      // 1. no cache and amount is a number? can't patch this rate :( )
      if (!cachedAmount) {
        return {
          amount: amount,
          currency: { code: 'EUR', rates: { EUR: 1, USD: 1, CZK: 1, CHF: 1, GBP: 1, PLN: 1 } }
        };
      }
      return { amount: amount, currency: cachedAmount.currency } as AmountInterface;
    }

    // 2. amount is AmountInterface and no previous rates found, use and save them
    if (!cachedAmount) {
      this.newestAmounts.push(amount);
      this.cacheAmounts();
    }
    // 3. amount is AmountInterface but cached rates are newer (also check handle undefined updatedAt)
    else if (
      cachedAmount.currency.updatedAt > amount.currency?.updatedAt ||
      (cachedAmount.currency.updatedAt && !amount.currency?.updatedAt)
    ) {
      amount.currency = cachedAmount.currency;
    }

    // 4. amount is AmountInterface and rates are newer than cached
    else if (cachedAmount.currency.updatedAt < amount.currency.updatedAt) {
      this.updateAmount(amount);
    }

    return amount;
  }

  /**
   * delete all amounts and the cache
   */
  clearRates() {
    this.newestAmounts = [];
    this.sessionStorage.write(this.sessionStorageName, JSON.stringify([]));
  }

  /**
   * find the newest rates for the given base currency. (should always be EUR but just in case)
   * @param currency the base currency you want the rates for
   * @returns an amountinterface containing the newest known rates for the given currency
   */
  private findNewestAmountForCurrency(currency: string): AmountInterface | null {
    return this.newestAmounts.find(predicate => predicate.currency.code === currency);
  }

  /**
   * replace the known rate with the given rate
   * @param newerAmount
   */
  private updateAmount(newerAmount: AmountInterface) {
    this.newestAmounts = this.newestAmounts.map(amount =>
      amount.currency.code === newerAmount.currency.code ? newerAmount : amount
    );
    this.rateChangeEvent.emit(newerAmount.currency);
    this.cacheAmounts();
  }

  private cacheAmounts() {
    this.sessionStorage.write(this.sessionStorageName, JSON.stringify(this.newestAmounts));
  }
}
